import {createContext, FC, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {WithChildren} from '../../helpers'

const MOBILE_BREAKPOINT = 992

type MobileSidebarContextValue = {
  isMobile: boolean
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

const MobileSidebarContext = createContext<MobileSidebarContextValue>({
  isMobile: false,
  isOpen: false,
  open: () => undefined,
  close: () => undefined,
  toggle: () => undefined,
})

const getIsMobile = () =>
  typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false

const MobileSidebarProvider: FC<WithChildren> = ({children}) => {
  const location = useLocation()
  const [isMobile, setIsMobile] = useState(getIsMobile)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const onResize = () => {
      const mobile = getIsMobile()
      setIsMobile(mobile)
      if (!mobile) setIsOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  const value = useMemo(
    () => ({isMobile, isOpen, open, close, toggle}),
    [isMobile, isOpen, open, close, toggle]
  )

  return <MobileSidebarContext.Provider value={value}>{children}</MobileSidebarContext.Provider>
}

const useMobileSidebar = () => useContext(MobileSidebarContext)

export {MobileSidebarProvider, useMobileSidebar}
